import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";

import { CompanyPicker, usePickedCompany } from "../components/CompanyPicker";
import { useToast } from "../components/Toast";
import type { WatchCreated } from "../lib/api";
import { useAddWatch, useMe, useRegistration } from "../lib/queries";
import { useBrand } from "../lib/brand";
import { useTitle } from "../lib/useTitle";

export function Home() {
  useTitle("Alertes redressement et liquidation d'entreprises");
  return (
    <div className="page">
      <section className="hero" aria-labelledby="hero-title">
        <div>
          <h1 id="hero-title">Sachez le jour même qu'un client passe au tribunal.</h1>
          <p className="hero__lede">
            Redressement, liquidation, radiation, changement de dirigeant : chaque matin, nous relisons le
            BODACC et le répertoire des entreprises pour les sociétés que vous suivez, et vous écrivons dès que
            quelque chose change.
          </p>
          <ul className="hero__facts">
            <li>Gratuit</li>
            <li>Sans mot de passe</li>
            <li>Sources publiques officielles</li>
          </ul>
        </div>
        <WatchForm />
      </section>
      <Triggers />
      <HowItWorks />
      <Sources />
      <Faq />
    </div>
  );
}

function WatchForm() {
  const me = useMe();
  const signedIn = Boolean(me.data);
  const navigate = useNavigate();
  const toast = useToast();
  const [siren, setSiren] = useState("");
  const [email, setEmail] = useState("");
  const [done, setDone] = useState<(WatchCreated & { email: string }) | null>(null);
  const picked = usePickedCompany(siren);
  const add = useAddWatch();
  const registration = useRegistration().data;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!picked.ready) return;
    add.mutate(signedIn ? { siren } : { siren, email }, {
      onSuccess: (result) => {
        if (signedIn) {
          toast(`${result.name ?? "L'entreprise"} est maintenant suivie.`);
          navigate(`/entreprise/${result.siren}`);
        } else {
          setDone({ ...result, email });
        }
      },
    });
  }

  if (done) {
    return (
      <div className="sheet watch-form" role="status">
        <div className="watch-form__done">
          <h2>
            {done.status === "pending_confirmation" ? "Plus qu'une étape : confirmez votre adresse" : "C'est noté"}
          </h2>
          {done.status === "pending_confirmation" ? (
            <p>
              Nous avons envoyé un lien à <strong>{done.email}</strong>. Cliquez dessus pour activer les alertes
              sur <strong>{done.name}</strong>. Pensez à regarder dans les indésirables.
            </p>
          ) : done.status === "pending_approval" ? (
            <p>
              <strong>{done.name}</strong> est ajoutée à la liste de <strong>{done.email}</strong>. Les alertes
              démarreront dès qu'un administrateur aura validé ce compte.
            </p>
          ) : (
            <p>
              <strong>{done.name}</strong> est ajoutée à la liste de <strong>{done.email}</strong>. Connectez-vous
              pour voir sa chronologie.
            </p>
          )}
          <div className="actions">
            <button
              type="button"
              className="button button--quiet"
              onClick={() => {
                setDone(null);
                setSiren("");
                add.reset();
              }}
            >
              Suivre une autre entreprise
            </button>
            {done.status === "active" && (
              <Link className="button" to="/connexion">
                Se connecter
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!signedIn && registration && !registration.open) {
    return (
      <div className="sheet watch-form">
        <div className="watch-form__done">
          <h2>Les inscriptions sont fermées</h2>
          <p>Le service n'accepte pas de nouveaux comptes pour le moment. Vous avez déjà un compte ?</p>
          <div className="actions">
            <Link className="button" to="/connexion">
              Se connecter
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form className="sheet watch-form" onSubmit={submit} noValidate aria-labelledby="watch-form-title">
      <h2 className="watch-form__title" id="watch-form-title">
        Quelle entreprise voulez-vous suivre ?
      </h2>
      <CompanyPicker siren={siren} onChange={setSiren} />
      {!signedIn && (
        <div className="field">
          <label className="field__label" htmlFor="watch-email">
            Adresse e-mail qui recevra les alertes
          </label>
          <input
            id="watch-email"
            className="input"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="vous@entreprise.fr"
          />
        </div>
      )}
      {add.isError && (
        <div className="notice notice--error" role="alert">
          {add.error.message}
        </div>
      )}
      <button
        type="submit"
        className="button watch-form__submit"
        disabled={!picked.ready || (!signedIn && !email.includes("@")) || add.isPending}
      >
        {add.isPending ? "Enregistrement" : "Surveiller cette entreprise"}
      </button>
      {!signedIn && (
        <p className="watch-form__fine">
          Vous recevrez un e-mail de confirmation.
          {registration?.approval_required && " Chaque nouveau compte est ensuite validé par un administrateur."} Chaque
          alerte contient un lien pour arrêter le suivi.
        </p>
      )}
    </form>
  );
}

const TRIGGERS = [
  {
    level: "critical",
    name: "Alerte immédiate",
    when: "L'entreprise risque de ne plus vous payer ou de disparaître.",
    events: ["Liquidation judiciaire", "Redressement judiciaire", "Cessation d'activité au répertoire"],
  },
  {
    level: "high",
    name: "Important",
    when: "Difficultés déclarées ou fin d'existence juridique.",
    events: ["Procédure de sauvegarde", "Conciliation", "Plan de cession ou de redressement", "Radiation du registre", "Rétablissement professionnel"],
  },
  {
    level: "medium",
    name: "À savoir",
    when: "L'entreprise change : vérifiez que vos contrats suivent.",
    events: ["Vente du fonds de commerce", "Transfert du siège", "Changement de dénomination", "Nouveaux dirigeants", "Changement de forme juridique ou d'activité"],
  },
  {
    level: "low",
    name: "Pour information",
    when: "Signes de vie administrative.",
    events: ["Dépôt des comptes annuels", "Autres annonces légales"],
  },
] as const;

function Triggers() {
  return (
    <section className="section" aria-labelledby="triggers-title">
      <div className="section__head">
        <h2 id="triggers-title">Ce qui déclenche un e-mail</h2>
        <p>
          Chaque événement est classé par gravité. Les plus graves passent en tête de l'e-mail et dans son objet.
        </p>
      </div>
      <ul className="triggers">
        {TRIGGERS.map((t) => (
          <li key={t.level} className={`triggers__row triggers__row--${t.level}`}>
            <div>
              <div className="triggers__level">{t.name}</div>
              <p className="triggers__when">{t.when}</p>
            </div>
            <ul className="triggers__events">
              {t.events.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="section" aria-labelledby="how-title">
      <div className="section__head">
        <h2 id="how-title">Comment ça marche</h2>
      </div>
      <div className="how">
        <ol className="steps">
          <li>
            <div>
              <h3>Indiquez l'entreprise</h3>
              <p>Par son SIREN ou par son nom. Nous vérifions qu'elle existe au répertoire.</p>
            </div>
          </li>
          <li>
            <div>
              <h3>Confirmez votre adresse</h3>
              <p>
                Un clic dans l'e-mail reçu active le suivi et vous connecte. Vous pouvez ensuite ajouter autant
                d'entreprises que nécessaire.
              </p>
            </div>
          </li>
          <li>
            <div>
              <h3>Recevez les alertes</h3>
              <p>
                Un seul e-mail par jour au maximum, uniquement s'il y a du nouveau, avec le détail du jugement et
                le lien vers l'annonce officielle.
              </p>
            </div>
          </li>
        </ol>
        <MailSample />
      </div>
    </section>
  );
}

function MailSample() {
  const { productName } = useBrand();
  return (
    <figure className="sheet mail-sample" aria-label="Exemple d'e-mail d'alerte">
      <dl className="mail-sample__head">
        <dt>De</dt>
        <dd>{productName}</dd>
        <dt>Objet</dt>
        <dd>ALERTE : Liquidation judiciaire, Atelier Exemple SAS</dd>
      </dl>
      <div className="mail-sample__body">
        <div className="mail-sample__company">Atelier Exemple SAS, Lyon</div>
        <div className="mail-sample__event">
          <span className="tag tag--critical">Liquidation judiciaire</span>
          <strong>Jugement d'ouverture de liquidation judiciaire</strong>
          <p>Publié au BODACC le 17 septembre 2026, Tribunal de commerce de Lyon</p>
          <p>
            Date de cessation des paiements : 5 août 2026. Liquidateur désigné : Me Exemple. Les déclarations de
            créances sont à adresser au liquidateur dans les deux mois suivant la publication.
          </p>
        </div>
        <span className="button button--small" aria-hidden="true">
          Voir la fiche de l'entreprise
        </span>
      </div>
    </figure>
  );
}

function Sources() {
  return (
    <section className="section" aria-labelledby="sources-title">
      <div className="section__head">
        <h2 id="sources-title">D'où viennent les informations</h2>
        <p>Uniquement des données publiques et officielles, consultées tous les jours.</p>
      </div>
      <div className="sources">
        <article>
          <h3>BODACC</h3>
          <p>
            Le Bulletin officiel des annonces civiles et commerciales publie les décisions des tribunaux et les
            actes des greffes.
          </p>
          <ul>
            <li>Ouvertures et jugements de procédures collectives</li>
            <li>Ventes et cessions de fonds</li>
            <li>Modifications, radiations, dépôts des comptes</li>
          </ul>
          <a href="https://www.bodacc.fr" target="_blank" rel="noopener noreferrer">
            bodacc.fr
          </a>
        </article>
        <article>
          <h3>Répertoire des entreprises</h3>
          <p>
            Les données Sirene de l'INSEE et du Registre national des entreprises, via l'API Recherche
            d'entreprises de l'État.
          </p>
          <ul>
            <li>État administratif : active ou cessée</li>
            <li>Siège, dénomination, forme juridique, activité</li>
            <li>Dirigeants</li>
          </ul>
          <a href="https://annuaire-entreprises.data.gouv.fr" target="_blank" rel="noopener noreferrer">
            annuaire-entreprises.data.gouv.fr
          </a>
        </article>
      </div>
    </section>
  );
}

const FAQ = [
  {
    q: "Pourquoi n'ai-je pas reçu d'alerte pour une procédure déjà ancienne ?",
    a: "Les annonces publiées avant le début de votre suivi apparaissent dans la chronologie de l'entreprise, mais ne déclenchent pas d'e-mail. Seules les nouveautés sont envoyées.",
  },
  {
    q: "Quel est le délai entre le jugement et l'alerte ?",
    a: "Un jugement est publié au BODACC en général une à deux semaines après avoir été rendu. Nous vérifions chaque matin : vous êtes prévenu au plus tard le lendemain de la publication.",
  },
  {
    q: "Combien d'entreprises puis-je suivre ?",
    a: "Autant que vous le souhaitez. Toutes les nouveautés du jour sont regroupées dans un seul e-mail.",
  },
  {
    q: "Comment arrêter les alertes ?",
    a: "Chaque e-mail contient un lien pour arrêter le suivi de l'entreprise concernée. Depuis votre espace, vous pouvez aussi retirer une entreprise ou supprimer votre compte.",
  },
  {
    q: "Les entreprises individuelles sont-elles couvertes ?",
    a: "Oui, si leurs informations sont publiques. Certains entrepreneurs ont demandé que leurs données ne soient pas diffusées : ils n'apparaissent alors pas dans la recherche.",
  },
  {
    q: "Ces informations ont-elles une valeur juridique ?",
    a: "Non. Ce service vous signale les publications ; seule l'annonce officielle au BODACC fait foi. Chaque alerte contient le lien vers celle-ci.",
  },
];

function Faq() {
  return (
    <section className="section" id="questions" aria-labelledby="faq-title">
      <div className="section__head">
        <h2 id="faq-title">Questions fréquentes</h2>
      </div>
      <div className="faq">
        {FAQ.map((item) => (
          <details key={item.q}>
            <summary>{item.q}</summary>
            <div>
              <p>{item.a}</p>
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}
